import { createAsyncThunk, createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { ApiProvider } from "@src/store/ApiProvider";
import axios from "axios";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

interface IGetPasswordParams {
	credentialId: number;
}

export interface IGetPasswordState {
	data: Record<number, string>;
	loading: boolean;
	error: string | null;
}

const initialState: IGetPasswordState = {
	data: {},
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const buildUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Credentials/Passwords`;
};

type TApiResponse = Array<{ credentialId: number; password: string }>;

export const getPassword = createAsyncThunk<{ credentialId: number; password: string }, IGetPasswordParams>(
	"passwordManager/getPassword",
	async ({ credentialId }, { rejectWithValue, dispatch }) => {
		try {
			// API expects JSON array body; send as raw string with spacing to match cURL -d '[ 81 ]'
			const body = `[ ${credentialId} ]`;
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<TApiResponse>(buildUrl(), body, {
					headers: getAuthHeaders(),
				}),
			);

			const payload = res.data ?? res;
			if (!Array.isArray(payload) || payload.length === 0 || typeof payload[0]?.password !== "string") {
				throw new Error("Unable to parse password response");
			}

			return { credentialId: payload[0].credentialId ?? credentialId, password: payload[0].password };
		} catch (error: unknown) {
			return rejectWithValue(error instanceof Error ? error.message : "Failed to fetch password");
		}
	},
);

const getPasswordSlice = createSlice({
	name: "passwordManager/getPassword",
	initialState,
	reducers: {
		setPassword: (state, action: PayloadAction<{ credentialId: number; password: string }>) => {
			const { credentialId, password } = action.payload;
			state.data[credentialId] = password;
		},
		clearPassword: (state, action: PayloadAction<number>) => {
			const credentialId = action.payload;
			state.data = Object.fromEntries(Object.entries(state.data).filter(([id]) => Number(id) !== credentialId));
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getPassword.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getPassword.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				const { credentialId, password } = action.payload;
				(state.data as Draft<Record<number, string>>)[credentialId] = password;
			})
			.addCase(getPassword.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to fetch password";
			});
	},
});

export const { setPassword: setCachedPassword, clearPassword: clearCachedPassword } = getPasswordSlice.actions;
export const getPasswordReducer = getPasswordSlice.reducer;
